import { Spine } from "@esotericsoftware/spine-pixi-v8";
import { sound } from "@pixi/sound";
import { gsap } from "gsap";
import { Color, Container, FillGradient, Text, TextStyle } from "pixi.js";
import { getbaseMeterVisibility, isSuperQuickBtnOnGlobal, landscapeMeterPositions, languageGlobal, portraitMeterPositions, RespinIndex } from "../../config/GlobalVariableConfig";
import { VISIBLE_SYMBOLS_PER_REEL } from "../../config/reelConfig";
import { DisplayObject, IDisplayObject } from "../../core/displays/DisplayObject";
import { Manager } from "../../core/Manager";
import { reelSpinEmitter } from "../../core/reels/ReelInit";
import { getJackpotEntryByIndex, wait } from "../../core/utils";
import { SoundConstants } from "../../gameClientEvents/SoundConstants";
import { spinData } from "../../server/getServerResposesFun";
export class Meter extends DisplayObject {
    private readonly METER_SKELETON = "jackpotMeterSkel";
    private readonly METER_ATLAS = "jackpotMeterAtlas";
    private readonly BASE_METER_SKELETON = "jackpotBaseMeterSkel";
    private readonly BASE_METER_ATLAS = "jackpotBaseMeterAtlas";
    private currentSequenceIndex: number = 0;
    private meterIds: number[] = [1, 2, 3, 4, 5, 6, 7, 8];
    private meters: Record<number, Spine> = {};
    private meterGroups: Container[] = [];
    private completedMeterCopies: Record<number, Container> = {};
    private baseMeter: Spine;
    private currentJackpotMeterLevel: number = 0;
    private currentWonJackpotValue: number = 0;

    private idleChainToken = 0;
    private isIdleChainRunning = false;
    private highlightedJackpotIndex: number | null = null;

    private createYellowGradient(): FillGradient {
        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);

        const colors = ["#fdea00", "#fbf8cb", "#fee500"].map((color) =>
            Color.shared.setValue(color).toNumber()
        );

        colors.forEach((number, index) => {
            fill.addColorStop(index / colors.length, number);
        });

        return fill;
    }

    private createBlueGradient(): FillGradient {
        const fill = new FillGradient(0, 0, 0, 32 * 1 * 3);

        const colors = ["#0047ff", "#7eb6ff", "#002ea6"].map((color) =>
            Color.shared.setValue(color).toNumber()
        );

        colors.forEach((number, index) => {
            fill.addColorStop(index / colors.length, number);
        });

        return fill;
    }

    private readonly DEFAULT_STYLE = new TextStyle({
        fill: this.createYellowGradient(),
        fontSize: 60,
        fontWeight: "bold",
        stroke: {
            color: "#000000",
            width: 3,
        },
    });

    private readonly ACTIVE_STYLE = new TextStyle({
        fill: this.createBlueGradient(),
        fontSize: languageGlobal === "es-es" ? 30 : 38,
        fontWeight: "bold",
        stroke: {
            color: "#040946",
            width: 2,
        },
    });
    private baseMeterYLand: number = 8;
    private baseMeterYPort: number = 690;
    private tweenOffset: number = 90;
    private baseMeterTextContainer: Container;
    private baseMeterPrefixText: Text;
    private baseMeterValueText: Text;

    constructor(config: IDisplayObject) {
        super(config);
        this.createBaseMeter();
        this.createInstances(this.meterIds);
        this.subscribeToEvents();
    }

    private async createInstances(symbolIds: number[], spacing: number = 3) {
        const startY: number = 0;
        const centerX: number = 0;
        const textYOffset = -98;

        const meterGroupLayering: Container[] = [];

        await wait(500);
        symbolIds.forEach((id, index) => {
            const meterGroup = new Container();
            meterGroup.x = centerX;
            meterGroup.y = startY - (spacing * index);
            // this.addChild(meterGroup);

            const spineAnim = Spine.from({
                skeleton: this.METER_SKELETON,
                atlas: this.METER_ATLAS,
            });
            spineAnim.label = "spine " + id.toString();
            meterGroup.addChild(spineAnim);

            const dataEntry = getJackpotEntryByIndex(index);
            const value = dataEntry.value || 0;
            const key = dataEntry.key || 0
            const keyText = new Text({ text: key, style: this.DEFAULT_STYLE.clone() });
            keyText.x = -100;
            keyText.y = -115 + (index * textYOffset);
            keyText.anchor.set(1, 0.5);
            keyText.label = "keyText " + id.toString();
            meterGroup.addChild(keyText);

            const valText = new Text({ text: value.toString() + "x", style: this.DEFAULT_STYLE.clone() });
            valText.x = 40;
            valText.y = -115 + (index * textYOffset);
            valText.anchor.set(0, 0.5);
            valText.label = "valueText " + id.toString();
            meterGroup.addChild(valText);

            spineAnim.state.setAnimation(0, `stage_${id}_idle`, false);

            this.meters[id] = spineAnim;
            this.meterGroups.push(meterGroup);
            meterGroupLayering.push(meterGroup);
        });
        // Set the layering of each meter
        for (let i = meterGroupLayering.length - 1; i >= 0; i--) {
            this.addChild(meterGroupLayering[i]);
        }
    }

    private createBaseMeter() {

        this.baseMeter = Spine.from({
            skeleton: this.BASE_METER_SKELETON,
            atlas: this.BASE_METER_ATLAS,
        });

        this.baseMeter.label = "baseMeter";

        this.baseMeter.visible = getbaseMeterVisibility();

        this.addChild(this.baseMeter);

        // Container
        this.baseMeterTextContainer = new Container();

        this.baseMeterTextContainer.label = "baseMeterTextContainer";

        this.baseMeterTextContainer.visible = false;

        this.addChild(this.baseMeterTextContainer);

        // Prefix Text
        this.baseMeterPrefixText = new Text({
            text: "CURRENT JACKPOT",
            // text: translationManager.game('current_jackpot'),
            style: new TextStyle({
                ...this.DEFAULT_STYLE,
                fontSize: 32,
            }),
        });

        this.baseMeterPrefixText.anchor.set(1, 0.5);

        // Value Text
        this.baseMeterValueText = new Text({
            text: "",
            style: new TextStyle({
                ...this.DEFAULT_STYLE,
                fontSize: 52,
            }),
        });

        this.baseMeterValueText.anchor.set(0.5, 0.5);

        // Positioning
        this.baseMeterPrefixText.x = 90;
        this.baseMeterPrefixText.y = 0;

        this.baseMeterValueText.x = 140;
        this.baseMeterValueText.y = -5;

        this.baseMeterTextContainer.addChild(
            this.baseMeterPrefixText,
            this.baseMeterValueText
        );

        this.baseMeter.state.setAnimation(0, "idle", true);

        this.baseMeter.position.set(0, 100);

        this.baseMeterTextContainer.position.set(
            this.baseMeter.x,
            this.baseMeter.y
        );
    }

    public override destroy(options?: boolean | undefined): void {
        this.unSubscribeToEvents();
        this.clearCompletedMeterCopies();
        super.destroy(options);
    }

    public activate(callback: () => void) {
        this.stopIdleChain();
        this.updateMeterText(0);
        const symbol = this.getMeterWithId(this.currentJackpotMeterLevel + 1);
        let timescale = isSuperQuickBtnOnGlobal ? 2 : 1;
        let listener = {
            complete: () => {
                symbol.state.removeListener(listener);
                symbol.state.timeScale = 1;
                callback?.();
            }
        };
        symbol.state.timeScale = timescale;
        symbol.state.addListener(listener);
        sound.play(SoundConstants.JACKPOT_UPDATE, { volume: 0.8 });
        symbol.state.setAnimation(0, `stage_${this.currentJackpotMeterLevel + 1}_active`, false);
        symbol.state.addAnimation(0, `stage_${this.currentJackpotMeterLevel + 1}_active_idle`, true);
        this.currentJackpotMeterLevel++;
    }

    public highlight(id: number, callback: () => void) {
        this.highlightedJackpotIndex = id;
        this.stopIdleChain();
        const symbol = this.getMeterWithId(id + 1);
        let timescale = isSuperQuickBtnOnGlobal ? 2 : 1;

        const shrinkSpeedMultiplier = 1.5;


        const shrinkListener = {
            complete: () => {
                symbol.state.removeListener(shrinkListener);
                symbol.state.setAnimation(0, `stage_${id + 1}_active_shrink_idle`, true);
                callback?.();
            }
        };

        const highlightListener = {
            complete: () => {
                symbol.state.removeListener(highlightListener);
                symbol.state.addListener(shrinkListener);
                const shrinkEntry = symbol.state.setAnimation(0, `stage_${id + 1}_active_shrink`, false);
                shrinkEntry.timeScale = shrinkSpeedMultiplier;
            }
        };

        symbol.state.timeScale = timescale;
        symbol.state.addListener(highlightListener);
        symbol.state.setAnimation(0, `stage_${id + 1}_active`, false);
        symbol.state.addAnimation(0, `stage_${id + 1}_active_idle`, true);
    }

    public deActivate(id: number) {
        const symbol = this.getMeterWithId(id);
        symbol.state.setAnimation(0, `stage_${id + 1}_idle`, true);
    }

    private subscribeToEvents() {
        if (spinData?.respins[RespinIndex.value]?.type === "fs_reveal") {
            return;
        }
        reelSpinEmitter.on("resetWins", this.onSpinReset, this);
    }

    private unSubscribeToEvents() {
        reelSpinEmitter.off("resetWins", this.onSpinReset, this);
    }

    /** Spin start: reset highlighted meter only; idle chain keeps running. */
    private onSpinReset() {
        if (this.highlightedJackpotIndex !== null) {
            this.resetMeterToStaticIdle(this.highlightedJackpotIndex);
            this.highlightedJackpotIndex = null;
        }
    }

    private resetMeterToStaticIdle(jackpotIndex: number) {
        const stageId = jackpotIndex + 1;
        const symbol = this.getMeterWithId(stageId);
        if (!symbol?.state) {
            return;
        }
        symbol.state.clearListeners();
        symbol.state.setAnimation(0, `stage_${stageId}_idle`, false);
        symbol.state.timeScale = 0;
    }

    public startIdleChain(force = true) {
        if (!force && this.highlightedJackpotIndex !== null) {
            return;
        }
        if (!force && this.isIdleChainRunning) {
            return;
        }

        this.idleChainToken++;

        this.currentSequenceIndex = 0;
        this.clearCompletedMeterCopies();
        this.stopIdleChain();

        const token = this.idleChainToken;
        this.isIdleChainRunning = true;
        this.playMeterAt(this.currentSequenceIndex, token);
    }

    private async playMeterAt(index: number, token: number) {

        if (token !== this.idleChainToken) {
            return;
        }
        if (index >= this.meterIds.length) {
            this.currentSequenceIndex = 0;
            const delay = Math.ceil(5000 * (Math.random() * 0.5 + 0.5));
            await wait(delay);
            if (token !== this.idleChainToken) {
                return;
            }
            this.playMeterAt(0, token);
            return;
        }

        const id = this.meterIds[index];
        const spine = this.getMeterWithId(id);
        if (!spine || !spine.state) {

            await wait(100);
            if (token !== this.idleChainToken) {
                return;
            }
            this.playMeterAt(index, token);
            return;
        }

        spine.state.addListener({
            event: (_: any, event: any) => {
                if (token !== this.idleChainToken) {
                    return;
                }

                if (event.data.name === "chenge") {
                    spine.state.clearListeners();
                    this.currentSequenceIndex++;
                    this.playMeterAt(this.currentSequenceIndex, token);
                }
            },

        });

        const trackEntry = spine.state.setAnimation(0, `stage_${id}_idle`, false);
        spine.state.timeScale = 2;
        trackEntry.mixDuration = 0.2;
    }

    public stopIdleChain() {
        this.idleChainToken++;
        this.isIdleChainRunning = false;

        Object.values(this.meters).forEach((s, index) => {
            s.state.clearListeners();
            s.state.setAnimation(0, `stage_${index + 1}_idle`, false);
            s.state.timeScale = 0;
        });
    }

    public updateMeterText(collectedScarabs: number, scarabRemaining?: number) {
        const level = (collectedScarabs > 0) ? this.currentJackpotMeterLevel - 1 : ((this.currentJackpotMeterLevel === 0) ? 0 : this.currentJackpotMeterLevel);
        const meterGroup = this.meterGroups[level];
        const jackpotData = getJackpotEntryByIndex(level);
        const key = meterGroup.getChildByName("keyText " + (level + 1)) as Text;
        const value = meterGroup.getChildByName("valueText " + (level + 1)) as Text;
        if (scarabRemaining !== undefined) {

            key.text = languageGlobal === "es-es" ? "REÚNE " + (scarabRemaining) : "COLLECT " + (scarabRemaining);
        } else {
            key.text = languageGlobal === "es-es" ? "REÚNE " + (jackpotData.key - collectedScarabs) : "COLLECT " + (jackpotData.key - collectedScarabs);
        }

        // if (scarabRemaining !== undefined) {
        //     key.text = translationManager.game("collect", null, {beetle: scarabRemaining});
        // } else {
        //     key.text = translationManager.game("collect", null, {beetle: (jackpotData.key - collectedScarabs)});
        // }

        value.text = languageGlobal === "es-es" ? "PARA GANAR " + jackpotData.value.toString() + "x" : "TO WIN " + jackpotData.value.toString() + "x";
        // value.text = translationManager.game("to_win", null, {multiplier: jackpotData.value.toString()}) + "x";
        // while(key.width > 240){
        //     key.style.fontSize -= 1;
        // }
        // while(value.width > 300){
        //     value.style.fontSize -= 1;
        // }
        if (collectedScarabs === 0) {
            this.updateMeterTextStyle(0);
        }
    }

    public upgradeCurrentJackpotValue(callback: () => void) {
        const level = this.currentJackpotMeterLevel - 1;
        const meterGroup = this.meterGroups[level];
        const jackpotData = getJackpotEntryByIndex(level);
        this.currentWonJackpotValue = jackpotData?.value ?? 0;
        const targetY = ((Manager.Orientation === "LANDSCAPE") ? this.baseMeterYLand : this.baseMeterYPort) + (this.tweenOffset * this.currentJackpotMeterLevel);
        const movingMeter = this.createMovingMeterReplica(level);
        movingMeter.label = "movingMeter";
        this.addChild(movingMeter);
        this.createCompletedMeterCopy(level);
        meterGroup.visible = false;
        gsap.to(movingMeter, {
            y: targetY + 40,
            duration: 0.3,
            ease: "none",
            onComplete: () => {
                const stageId = level + 1;
                const movingSpine = movingMeter.getChildByName(
                    `spine ${stageId}`
                ) as Spine;
                sound.play(SoundConstants.METER_HIT_BASEMETER, { volume: 0.8 });
                reelSpinEmitter.emit('shakeEffect');
                if (movingSpine) {
                    const keyText = movingMeter.getChildByName(`keyText ${stageId}`);
                    const valueText = movingMeter.getChildByName(`valueText ${stageId}`);
                    if (keyText) {
                        movingMeter.removeChild(keyText);
                        keyText.destroy();
                    }
                    if (valueText) {
                        movingMeter.removeChild(valueText);
                        valueText.destroy();
                    }
                    this.baseMeterPrefixText.text = languageGlobal === "es-es" ? "JACKPOT ACTUAL " : "CURRENT JACKPOT";
                    // this.baseMeterPrefixText.text = translationManager.game("current_jackpot");

                    this.baseMeterValueText.text = jackpotData.value + "x";

                    this.baseMeterPrefixText.style.fontSize = 28;

                    this.baseMeterValueText.style.fontSize = 42;

                    this.baseMeterTextContainer.visible = true;

                    gsap.fromTo(
                        this.baseMeterValueText.scale,
                        {
                            x: 0.5,
                            y: 0.5,
                        },
                        {
                            x: 1,
                            y: 1,
                            duration: 0.3,
                            ease: "back.out(2)",
                        }
                    );
                    this.baseMeter.state.setAnimation(0, "win", false).listener = {
                        complete: () => {
                            this.baseMeter.state.addAnimation(0, "idle", true);
                        }
                    };
                    movingSpine.state.setAnimation(
                        0,
                        `stage_${stageId}_blast`,
                        false
                    ).listener = {
                        complete: async () => {
                            movingMeter.destroy({ children: true });
                            // await wait(500);
                            callback?.();
                        }
                    };
                };
            }
        })
    };

    private createMovingMeterReplica(level: number): Container {
        const sourceMeter = this.meterGroups[level];
        const stageId = level + 1;

        const keySource = sourceMeter.getChildByName(`keyText ${stageId}`) as Text;
        const valueSource = sourceMeter.getChildByName(`valueText ${stageId}`) as Text;

        const replica = new Container();
        replica.position.copyFrom(sourceMeter.position);

        const spineReplica = Spine.from({
            skeleton: this.METER_SKELETON,
            atlas: this.METER_ATLAS,
        });

        spineReplica.label = `spine ${stageId}`;
        spineReplica.state.setAnimation(0, `stage_${stageId}_active_idle`, true);

        replica.addChild(spineReplica);

        const keyReplica = new Text({
            text: keySource.text,
            style: keySource.style.clone(),
        });

        keyReplica.label = `keyText ${stageId}`;
        keyReplica.position.set(keySource.x, keySource.y);
        keyReplica.anchor.set(keySource.anchor.x, keySource.anchor.y);

        replica.addChild(keyReplica);

        const valueReplica = new Text({
            text: valueSource.text,
            style: valueSource.style.clone(),
        });

        valueReplica.label = `valueText ${stageId}`;
        valueReplica.position.set(valueSource.x, valueSource.y);
        valueReplica.anchor.set(valueSource.anchor.x, valueSource.anchor.y);

        replica.addChild(valueReplica);

        return replica;
    }

    public override setPositionResize(orientation: string) {

        const symbolCount = VISIBLE_SYMBOLS_PER_REEL;
        const index = symbolCount - 3;

        if (orientation === "LANDSCAPE") {
            const pos = landscapeMeterPositions()[index][symbolCount];
            this.setPosition(pos.posX, pos.posY);
            this.setScale(this.config.landscape.scale, this.config.landscape.scale);
        } else {
            const pos = portraitMeterPositions[index][symbolCount];
            this.setPosition(pos.posX, pos.posY);
            this.setScale(this.config.portrait.scale, this.config.portrait.scale);
        }

        if (orientation === "LANDSCAPE") {
            this.baseMeter?.position.set(0, this.baseMeterYLand);
        } else {
            this.baseMeter?.position.set(0, this.baseMeterYPort);
        }
        if (this.baseMeterTextContainer) {

            this.baseMeterTextContainer.x = this.baseMeter.x;

            this.baseMeterTextContainer.y = this.baseMeter.y;
        }
        this.updateCompletedMetersVisibility(orientation);
    }

    public getCurrentActiveMeter() {
        return this.getMeterWithId(this.currentJackpotMeterLevel);
    }

    public getCurrentBaseGameActiveMeter(id: number) {
        return this.getMeterWithId(id + 1);
    }

    public getActiveMeterId() {
        return this.currentJackpotMeterLevel;
    }

    public getCurrentWonJackpotValue(): number {
        return this.currentWonJackpotValue;
    }

    private updateMeterTextStyle(collectedScarabs: number) {
        const level = (collectedScarabs > 0) ? this.currentJackpotMeterLevel + 1 : ((this.currentJackpotMeterLevel === 0) ? 0 : this.currentJackpotMeterLevel);
        const meterGroup = this.meterGroups[level];
        const key = meterGroup.getChildByName("keyText " + (level + 1)) as Text;
        const value = meterGroup.getChildByName("valueText " + (level + 1)) as Text;
        key.style = this.ACTIVE_STYLE.clone();
        value.style = this.ACTIVE_STYLE.clone();
    }

    private getMeterWithId(id: number): Spine {
        return this.meters[id];
    }

    // private createMeterReplica(level: number): Container {
    //     const sourceMeter = this.meterGroups[level];
    //     const stageId = level + 1;

    //     const replica = new Container();
    //     replica.position.copyFrom(sourceMeter.position);

    //     const spineReplica = Spine.from({
    //         skeleton: this.METER_SKELETON,
    //         atlas: this.METER_ATLAS,
    //     });

    //     spineReplica.label = `spine ${stageId}`;

    //     // Use normal idle stage
    //     spineReplica.state.setAnimation(0, `stage_${stageId}_idle`, true);

    //     // Freeze animation completely
    //     spineReplica.state.timeScale = 0;

    //     replica.addChild(spineReplica);

    //     return replica;
    // }

    private createCompletedMeterCopy(level: number) {
        this.completedMeterCopies[level]?.destroy({ children: true });

        const stageId = level + 1;
        const jackpotData = getJackpotEntryByIndex(level);

        const completedCopy = new Container();
        completedCopy.label = `completedMeter ${stageId}`;

        const spineCopy = Spine.from({
            skeleton: this.METER_SKELETON,
            atlas: this.METER_ATLAS,
        });

        spineCopy.label = `spine ${stageId}`;
        spineCopy.state.setAnimation(0, `stage_${stageId}_idle`, true);
        spineCopy.state.timeScale = 0;
        spineCopy.tint = 0x555555;
        spineCopy.alpha = 0.75;

        completedCopy.addChild(spineCopy);

        const keyText = new Text({
            text: `${jackpotData.key}`,
            style: this.DEFAULT_STYLE.clone(),
        });

        keyText.x = -100;
        keyText.y = -115 + (level * -98);
        keyText.anchor.set(1, 0.5);
        keyText.tint = 0x555555;

        completedCopy.addChild(keyText);

        const valueText = new Text({
            text: `${jackpotData.value}x`,
            style: this.DEFAULT_STYLE.clone(),
        });

        valueText.x = 40;
        valueText.y = -115 + (level * -98);
        valueText.anchor.set(0, 0.5);
        valueText.tint = 0x555555;

        completedCopy.addChild(valueText);

        completedCopy.visible = Manager.Orientation === "LANDSCAPE";
        this.completedMeterCopies[level] = completedCopy;
        this.attachCompletedCopyBehindReelFrame(completedCopy, level);
    }

    private clearCompletedMeterCopies() {
        Object.values(this.completedMeterCopies).forEach((copy) => copy.destroy({ children: true }));
        this.completedMeterCopies = {};
        this.meterGroups.forEach((group) => {
            group.visible = true;
        });
    }

    private attachCompletedCopyBehindReelFrame(copy: Container, level: number) {
        const sourceMeter = this.meterGroups[level];
        copy.position.copyFrom(sourceMeter.position);
        this.addChildAt(copy, 0);
    }

    private updateCompletedMetersVisibility(orientation: string) {
        if (!this.completedMeterCopies) return;
        const shouldShowCompletedMeters = orientation === "LANDSCAPE";
        Object.values(this.completedMeterCopies).forEach((copy) => {
            copy.visible = shouldShowCompletedMeters;
        });
    }
}
